/**
 * MegaMenu Plugin
 * ES6 accessible megamenu with ARIA support.
 * Closes on outside click, scroll, and Escape key.
 */

class MegaMenu {
  #nav;
  #items;
  #activeItem = null;
  #scrollHandler;
  #outsideClickHandler;
  #keydownHandler;

  /**
   * @param {string|Element} selector - CSS selector or DOM element for the <nav>
   * @param {object} [options]
   * @param {string} [options.navActiveClass='mainnav-active']
   * @param {string} [options.subnavActiveClass='subnav-active']
   */
  constructor(selector, options = {}) {
    this.#nav = typeof selector === "string" ? $(document, selector) : selector;

    if (!this.#nav) {
      console.warn(`MegaMenu: element not found for selector "${selector}"`);
      return;
    }

    this.options = {
      navActiveClass: "mainnav-active",
      subnavActiveClass: "subnav-active",
      ...options,
    };

    this.#init();
  }

  // ─── Private ─────────────────────────────────────────────────────────────────

  #init() {
    this.#items = [...this.#nav.querySelectorAll(":scope > ul > li")];

    this.#items.forEach(item => {
      const trigger = $(item, ":scope > h2 > a");
      const subnav = $(item, ":scope > .siteheader-subnav");

      if (!trigger) return;

      if (subnav) {
        // ARIA setup
        const id = `megamenu-${Math.random().toString(36).slice(2, 8)}`;
        subnav.id = id;
        trigger.setAttribute("aria-haspopup", "true");
        trigger.setAttribute("aria-expanded", "false");
        trigger.setAttribute("aria-controls", id);
        subnav.setAttribute("role", "region");
        subnav.setAttribute(
          "aria-label",
          $(trigger, "span")?.textContent?.trim() ?? ""
        );

        trigger.addEventListener("click", e => {
          e.preventDefault();
          this.#toggle(item, trigger, subnav);
        });

        // Keyboard: open on Enter/Space, close on Escape
        trigger.addEventListener("keydown", e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.#toggle(item, trigger, subnav);
          }
        });
      }
    });

    // Global handlers
    this.#scrollHandler = () => this.#close();
    this.#outsideClickHandler = e => {
      if (this.#activeItem && !this.#nav.contains(e.target)) {
        this.#close();
      }
    };
    this.#keydownHandler = e => {
      if (e.key === "Escape") this.#close();
    };

    window.addEventListener("scroll", this.#scrollHandler, { passive: true });
    document.addEventListener("click", this.#outsideClickHandler);
    document.addEventListener("keydown", this.#keydownHandler);
  }

  #toggle(item, trigger, subnav) {
    const isOpen = item.classList.contains(this.options.navActiveClass);

    // Close any currently open item first
    this.#close();

    if (!isOpen) {
      this.#open(item, trigger, subnav);
    }
  }

  #open(item, trigger, subnav) {
    item.classList.add(this.options.navActiveClass);
    subnav.classList.add(this.options.subnavActiveClass);
    trigger.setAttribute("aria-expanded", "true");
    this.#activeItem = { item, trigger, subnav };
  }

  #close() {
    if (!this.#activeItem) return;
    const { item, trigger, subnav } = this.#activeItem;
    item.classList.remove(this.options.navActiveClass);
    subnav.classList.remove(this.options.subnavActiveClass);
    trigger.setAttribute("aria-expanded", "false");
    this.#activeItem = null;
  }

  // ─── Public API ───────────────────────────────────────────────────────────────

  /** Programmatically close the open megamenu panel. */
  close() {
    this.#close();
  }

  /** Tear down all event listeners. */
  destroy() {
    window.removeEventListener("scroll", this.#scrollHandler);
    document.removeEventListener("click", this.#outsideClickHandler);
    document.removeEventListener("keydown", this.#keydownHandler);

    this.#items.forEach(item => {
      const trigger = $(item, ":scope > h2 > a");
      const subnav = $(item, ":scope > .siteheader-subnav");
      if (!trigger || !subnav) return;
      trigger.removeAttribute("aria-haspopup");
      trigger.removeAttribute("aria-expanded");
      trigger.removeAttribute("aria-controls");
      subnav.removeAttribute("id");
      subnav.removeAttribute("role");
      subnav.removeAttribute("aria-label");
    });
  }
}

// ─── Auto-init & export ───────────────────────────────────────────────────────

// export default MegaMenu;

/**
 * Auto-initialise if a [data-megamenu] nav is present in the DOM.
 *
 * Usage (HTML): <nav class="siteheader-nav" data-megamenu>…</nav>
 * Usage (JS module):
 *   import MegaMenu from './megamenu.js';
 *   const menu = new MegaMenu('.siteheader-nav');
 */
if (typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => {
    $$(document, "[data-megamenu]").forEach(nav => {
      nav._megaMenu = new MegaMenu(nav);
    });
  });
}
